import { ChangeDetectionStrategy, Component, ElementRef, effect, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  ButtonCloseDirective,
  ButtonDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
} from '@coreui/angular-pro';
import { TwoFactorEnforcementService } from './two-factor-enforcement.service';

/**
 * One-Shot-Modal nach dem ersten Login pro Browser-Session, das den
 * User auf die 2FA-Pflicht hinweist und ihn direkt zur Sicherheits-Seite
 * leiten kann. "Später" merkt sich die Wegklick-Aktion via sessionStorage —
 * der Banner bleibt davon unbeeinflusst.
 */
@Component({
  selector: 'app-two-factor-required-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    ButtonCloseDirective,
    ModalComponent,
    ModalHeaderComponent,
    ModalTitleDirective,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  template: `
    @if (svc.modalOpen()) {
      <c-modal
        [visible]="true"
        (visibleChange)="onVisibleChange($event)"
        [attr.aria-labelledby]="titleId"
        [attr.aria-describedby]="bodyId"
        backdrop="static"
      >
        <c-modal-header>
          <h5 cModalTitle [id]="titleId">Zwei-Faktor-Authentifizierung einrichten</h5>
          <button
            cButtonClose
            type="button"
            aria-label="Hinweis schliessen"
            (click)="later()"
          ></button>
        </c-modal-header>
        <c-modal-body [id]="bodyId">
          <p class="mb-2">
            Für deine Rolle ist Zwei-Faktor-Authentifizierung (2FA) verpflichtend.
            Sie schützt deinen Account zusätzlich zum Passwort vor Übernahme durch
            Dritte.
          </p>
          @if (svc.isOverdue()) {
            <p class="alert alert-danger small mb-2">
              <strong>Frist abgelaufen.</strong> Bitte richte 2FA jetzt ein.
            </p>
          } @else if (svc.graceDeadlineLabel(); as deadline) {
            <p class="alert alert-warning small mb-2">
              Frist zur Einrichtung: <strong>{{ deadline }}</strong>
              @if (svc.daysRemaining() !== null) {
                (noch {{ svc.daysRemaining() }} {{ svc.daysRemaining() === 1 ? 'Tag' : 'Tage' }})
              }.
            </p>
          }
          <p class="small text-muted mb-0">
            Du brauchst eine Authenticator-App auf deinem Smartphone — z. B.
            Google Authenticator, Microsoft Authenticator, 1Password oder Bitwarden.
            Die Einrichtung dauert ca. 2 Minuten.
          </p>
        </c-modal-body>
        <c-modal-footer>
          <button
            #laterBtn
            cButton
            type="button"
            color="ghost"
            (click)="later()"
          >
            Später
          </button>
          <button
            cButton
            type="button"
            color="primary"
            (click)="goToSetup()"
          >
            Jetzt einrichten
          </button>
        </c-modal-footer>
      </c-modal>
    }
  `,
})
export class TwoFactorRequiredModalComponent {
  protected readonly svc = inject(TwoFactorEnforcementService);
  private readonly router = inject(Router);

  protected readonly titleId = 'two-factor-required-title';
  protected readonly bodyId = 'two-factor-required-body';

  private readonly laterBtn = viewChild<ElementRef<HTMLButtonElement>>('laterBtn');

  constructor() {
    effect(() => {
      const open = this.svc.modalOpen();
      const ref = this.laterBtn();
      if (open && ref) {
        queueMicrotask(() => ref.nativeElement.focus());
      }
    });
  }

  protected later(): void {
    this.svc.dismissModalForSession();
  }

  protected goToSetup(): void {
    this.svc.closeModalAfterAction();
    this.router.navigate(['/profil/sicherheit']);
  }

  protected onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.svc.dismissModalForSession();
    }
  }
}
